import { FormEvent, useEffect, useState } from "react";
import { Eye, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminPaymentTransaction, AdminUser, PaginationMeta, getCurrentAdmin, listPaymentTransactions, logout } from "./api";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

const statusLabels: Record<string, string> = {
  Created: "已建立（尚未回應）",
  Success: "成功",
  Failed: "失敗",
};

const statusBadgeClass: Record<string, string> = {
  Created: "bg-[#f3ecdf] text-[#8a6a2f]",
  Success: "bg-[#e9f4ec] text-[#245438]",
  Failed: "bg-[#fdebea] text-[#9a2d2d]",
};

/** 金流交易紀錄查詢：純讀取，金流交易由系統在建立付款／收到金流商回呼時自動寫入，見
 * AdminPaymentTransactionApiController 說明，不提供後台手動新增或修改。 */
export default function AdminPaymentTransactions() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [transactions, setTransactions] = useState<AdminPaymentTransaction[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");
  const [gatewayProvider, setGatewayProvider] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");

  async function load(filters: { StartDate?: string; EndDate?: string; Status?: string; GatewayProvider?: string; Keyword?: string; Page?: number; PerPage?: number } = {}) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listPaymentTransactions({
        StartDate: filters.StartDate ?? startDate,
        EndDate: filters.EndDate ?? endDate,
        Status: filters.Status ?? status,
        GatewayProvider: filters.GatewayProvider ?? gatewayProvider,
        Keyword: filters.Keyword ?? keyword.trim(),
        Page: filters.Page ?? pagination.Page,
        PerPage: filters.PerPage ?? pagination.PerPage,
      });
      setTransactions(data.Transactions);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "金流交易紀錄載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ StartDate: startDate, EndDate: endDate, Status: status, GatewayProvider: gatewayProvider, Keyword: keyword.trim(), Page: 1 });
  }

  function handleReset() {
    setStartDate("");
    setEndDate("");
    setStatus("");
    setGatewayProvider("");
    setKeyword("");
    load({ StartDate: "", EndDate: "", Status: "", GatewayProvider: "", Keyword: "", Page: 1 });
  }

  return (
    <AdminLayout sectionLabel="帳務管理" title="金流交易紀錄查詢" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="pt-start">起始日期</label>
          <input id="pt-start" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="pt-end">結束日期</label>
          <input id="pt-end" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="pt-status">交易狀態</label>
          <select id="pt-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Created">已建立（尚未回應）</option>
            <option value="Success">成功</option>
            <option value="Failed">失敗</option>
          </select>
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="pt-gateway">金流商</label>
          <select id="pt-gateway" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={gatewayProvider} onChange={(event) => setGatewayProvider(event.target.value)}>
            <option value="">全部</option>
            <option value="ECPay">綠界 ECPay</option>
            <option value="NewebPay">藍新 NewebPay</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[220px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="pt-keyword">關鍵字</label>
          <input id="pt-keyword" className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="訂單編號、交易編號" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={handleReset} type="button">
          <RefreshCw size={18} />
          清除條件
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1080px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[160px] px-4 py-3">時間</th>
                <th className="w-[180px] px-4 py-3">訂單編號</th>
                <th className="w-[110px] px-4 py-3">金流商</th>
                <th className="w-[220px] px-4 py-3">交易編號</th>
                <th className="w-[140px] px-4 py-3">金額</th>
                <th className="w-[140px] px-4 py-3">狀態</th>
                <th className="w-[90px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>沒有符合條件的交易紀錄。</td></tr>
              ) : transactions.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.PaymentTransactionId}>
                  <td className="px-4 py-4 text-[#59665f]">{item.CreatedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-[#203d32]">{item.OrderNumber || "-"}</p>
                    {item.RecipientName && <p className="mt-1 text-xs text-[#7b877f]">{item.RecipientName}</p>}
                  </td>
                  <td className="px-4 py-4 text-[#59665f]">{item.GatewayProvider || "-"}</td>
                  <td className="px-4 py-4">
                    <p className="truncate text-[#59665f]">{item.TradeNumber}</p>
                    {item.GatewayTransactionId && <p className="mt-1 truncate text-xs text-[#7b877f]">金流商序號：{item.GatewayTransactionId}</p>}
                  </td>
                  <td className="px-4 py-4 font-bold text-[#203d32]">NT$ {item.Amount.toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClass[item.Status] || ""}`}>
                      {statusLabels[item.Status] || item.Status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/orders/${item.OrderId}`)} type="button">
                      <Eye size={16} />查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Page: page })} />
      </div>
    </AdminLayout>
  );
}
